// @file Composable to manage surface navbar visibility state, specifically for AI chat visibility
import { isInClosedApproveAllButtonMap, updateUserClosedApproveAllButtonMap } from '@@/bits/approve_all_button'
import { isAppUsing } from '@@/bits/flip'
import { getSearchParam } from '@@/bits/location'
import { isRegistered } from '@@/bits/user_model'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceAccountsStore } from '@@/pinia/surface_accounts_store'
import { useSurfaceAIChatStore } from '@@/pinia/surface_ai_chat_store'
import { useCommentsStore } from '@@/pinia/surface_comments'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useSurfaceGuestStore } from '@@/pinia/surface_guest_store'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import { useSurfaceRemakeLinkStore } from '@@/pinia/surface_remake_link_store'
import { TRANSLATION_QUERY_PARAM_KEY, useSurfaceTranslationsStore } from '@@/pinia/surface_translations_store'
import { useWindowSizeStore } from '@@/pinia/window_size'
import { createSharedComposable, tryOnMounted } from '@vueuse/core'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useReducedMotionMode } from './useReducedMotionMode'

/**
 * ID for the avatar button element used as anchor for guest ID card or user menu popover.
 * This should be assigned to the avatar button in both SurfaceTopBar and SurfaceActionBar.
 */
export const AVATAR_EL_ID = 'guest-id-card-avatar-anchor'

export const useSurfaceNavbarVisibility = createSharedComposable(() => {
  const surfaceStore = useSurfaceStore()
  const surfaceAccountsStore = useSurfaceAccountsStore()
  const surfaceAIChatStore = useSurfaceAIChatStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfaceTranslationsStore = useSurfaceTranslationsStore()
  const demoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()
  const surfaceGuestStore = useSurfaceGuestStore()
  const windowSizeStore = useWindowSizeStore()
  const surfacePostsStore = useSurfacePostsStore()
  const commentsStore = useCommentsStore()
  const surfaceRemakeLinkStore = useSurfaceRemakeLinkStore()
  const surfaceCurrentUserStore = useSurfaceCurrentUserStore()

  // #region Nav bar mounted state
  /**
   * Used for:
   * - displaying the add post button, and then teleporting the add post button to the nav bar.
   */
  const isActionBarMounted = ref(false)
  function setIsActionBarMounted(newValue: boolean): void {
    isActionBarMounted.value = newValue
  }
  // #endregion

  const xAiChat = computed(() => {
    if (!surfacePermissionsStore.amIOwner) return false
    if (!demoPadletPanelStore.demoPadletPanelVisibility.showSurfaceAiChat) return false
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) return false

    // For users that specifically enabled surface chat or are native admins, we show it
    if (isAppUsing('surfaceAiChat') || surfaceAccountsStore.isNativeAdmin) {
      return true
    }

    if (!surfaceStore.isMagicWall) return false
    if (surfaceStore.magicWallType === 'learning_assessment_polls') return false

    // Else, we show it for library and backpack teachers.
    if (surfaceAccountsStore.isLibraryTeacherOrAbove || surfaceAccountsStore.isBackpackTeacherOrAbove) return true

    return false
  })

  const xSignup = computed(() => {
    return isAppUsing('signUpSurface') && !isRegistered(surfaceStore.user) && !surfaceStore.isSimplifiedView
  })

  const xGuestIdCard = computed(() => {
    return (
      surfaceGuestStore.shouldEnableAnonymousAttribution &&
      !surfacePermissionsStore.amIRegistered &&
      surfaceGuestStore.xGuestIdCard &&
      !windowSizeStore.isSmallerThanTabletPortrait &&
      !surfaceGuestStore.isGuestIdLabelExpanded
    )
  })

  const xActivityPanelButton = computed<boolean>(() => {
    const wallBuilderId = surfaceStore.wallAttributes?.builder?.id
    if (wallBuilderId === null) return false

    // Check if there are posts from users other than the wall builder
    const hasPostsFromOthers = surfacePostsStore.publishedPosts.some((post) => post.author_id !== wallBuilderId)
    if (hasPostsFromOthers) return true

    // Check if there are comments from users other than the wall builder
    const hasCommentsFromOthers = Object.values(commentsStore.commentEntities).some(
      (comment) => comment.user_id !== wallBuilderId,
    )
    if (hasCommentsFromOthers) return true

    // Check if there are walls that have been remade from this wall
    if (surfaceRemakeLinkStore.hasWallsRemade) return true

    return false
  })

  // #region Translation
  const xTranslationUi = computed(
    () => !surfaceTranslationsStore.isDetectingLanguage && surfaceTranslationsStore.xTranslateToUserLocaleBanner,
  )
  const xTranslatedCheckmark = computed(() => surfaceTranslationsStore.isViewingTranslatedPadlet)

  /**
   * Tracks if user has already initiated translation on page load.
   * If true, we should hide the banner initially to prevent flashing.
   */
  const hasInitiatedTranslationOnMount = ref<boolean>(getSearchParam(TRANSLATION_QUERY_PARAM_KEY) != null)

  // Reset the flag after initial translation completes to allow normal banner behavior
  watch(
    () =>
      surfaceTranslationsStore.isTranslatingPadletFromBanner || surfaceTranslationsStore.isTranslatingPadletFromPanel,
    (isTranslating, wasTranslating) => {
      // When translation finishes (was true, now false) and we had initiated on mount, reset the flag
      if (wasTranslating && !isTranslating && hasInitiatedTranslationOnMount.value) {
        hasInitiatedTranslationOnMount.value = false
      }
    },
  )

  const xTranslationBanner = computed(
    () => xTranslationUi.value && surfaceTranslationsStore.isBannerExpanded && !hasInitiatedTranslationOnMount.value,
  )

  const { currentUser } = storeToRefs(surfaceCurrentUserStore)
  const { isInReducedMotionMode } = useReducedMotionMode(currentUser)
  const bannerCollapseDelay = computed(() => (isInReducedMotionMode.value ? 0 : 500))
  /**
   * Delayed version of isBannerExpanded for coordinating animations.
   * When the banner collapses, we delay showing the globe button by bannerCollapseDelay
   * to allow the SurfaceTopBarBannerTransition to complete first.
   */
  const isBannerCollapsedWithDelay = ref<boolean>(false)
  let bannerCollapseTimeout: ReturnType<typeof setTimeout> | null = null

  watch(
    () => surfaceTranslationsStore.isBannerExpanded,
    (isExpanded, wasExpanded) => {
      if (bannerCollapseTimeout) {
        clearTimeout(bannerCollapseTimeout)
        bannerCollapseTimeout = null
      }

      if (!isExpanded) {
        // Only delay if this is a transition from expanded to collapsed
        if (wasExpanded === true) {
          // Banner is collapsing - delay the state update by bannerCollapseDelay (SurfaceTopBarBannerTransition leave duration)
          bannerCollapseTimeout = setTimeout(() => {
            isBannerCollapsedWithDelay.value = true
            bannerCollapseTimeout = null
          }, bannerCollapseDelay.value)
        } else {
          // Banner is already collapsed (initial state) - no delay needed
          isBannerCollapsedWithDelay.value = true
        }
      } else {
        // Banner is expanding - update immediately
        isBannerCollapsedWithDelay.value = false
      }
    },
    { immediate: true },
  )

  const xOpenTranslationPanelButton = computed(() => {
    // If user is viewing translated padlet, always show the button
    // (they need to be able to change language or revert, regardless of current locale)
    if (surfaceTranslationsStore.isViewingTranslatedPadlet) {
      return true
    }

    // For all other cases, check if translation UI is available
    if (!xTranslationUi.value) return false

    // If banner is currently expanded, don't show the globe button yet
    // (user is seeing the banner)
    if (surfaceTranslationsStore.isBannerExpanded) {
      return false
    }

    // If banner has been collapsed (either by close button or translate action),
    // wait for the collapse animation to complete before showing the globe button
    if (!isBannerCollapsedWithDelay.value) {
      return false
    }

    // Show globe button after banner collapse animation completes if:
    // - User has dismissed the banner, OR
    // - User is translating from panel, OR
    // - User initiated translation from banner
    if (
      surfaceTranslationsStore.hasDismissedTranslationBanner ||
      surfaceTranslationsStore.isTranslatingPadletFromPanel ||
      surfaceTranslationsStore.isTranslatingPadletFromBanner
    ) {
      return true
    }

    return false
  })
  // #endregion

  // #region Approval
  const shouldCloseApproveAllBanner = ref(isInClosedApproveAllButtonMap(surfaceStore.wallHashid))
  const numUnapprovedContributions = computed(() => {
    return surfacePostsStore.unapprovedPosts.length + commentsStore.unapprovedComments.length
  })

  const xApprovalBanner = computed(
    () =>
      numUnapprovedContributions.value >= 3 &&
      surfacePermissionsStore.canIAdminister &&
      !shouldCloseApproveAllBanner.value &&
      !surfaceStore.isFrozen &&
      !surfaceTranslationsStore.isViewingTranslatedPadlet,
  )

  watch(numUnapprovedContributions, (newVal) => {
    if (newVal < 3) {
      shouldCloseApproveAllBanner.value = true
    }
  })

  watch([() => surfacePostsStore.remotePosts, () => commentsStore.remoteComments], () => {
    // we only need to handle remote contribution editing and adding to distinguish from
    // initial fetch for posts/comments since fetching for posts/comments wont reduce numUnapprovedContributions
    if (numUnapprovedContributions.value >= 3) {
      updateUserClosedApproveAllButtonMap(surfaceStore.wallHashid, false)
      shouldCloseApproveAllBanner.value = false
    }
  })

  const closeApprovalBanner = () => {
    shouldCloseApproveAllBanner.value = true
    updateUserClosedApproveAllButtonMap(surfaceStore.wallHashid, true)
  }
  // #endregion

  // #region Notification banners
  const xNotificationBannerList = computed(() => xTranslationBanner.value || xApprovalBanner.value)
  // #endregion

  const initializeAiChat = async () => {
    await surfaceAccountsStore.initialize()

    // Load data to show feedback button, remove wall options if first time
    await surfaceAIChatStore.loadMagicWallFirstOpen(xAiChat.value)
  }

  tryOnMounted(async () => {
    await initializeAiChat()
  })

  return {
    xAiChat,
    xSignup,
    xGuestIdCard,
    xActivityPanelButton,
    isActionBarMounted,
    setIsActionBarMounted,
    xTranslationBanner,
    xOpenTranslationPanelButton,
    xTranslatedCheckmark,
    xApprovalBanner,
    closeApprovalBanner,
    xNotificationBannerList,
  }
})
